import { ChevronLeft, ChevronRight, Menu, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { RealtimeIndicator } from '@/components/layout/RealtimeIndicator';
import { Button } from '@/components/ui/button';
import { useBreadcrumbs } from '@/context/breadcrumbs';
import { useTheme } from '@/lib/theme';
import { useAuth } from '@/lib/use-auth';

export function AppBar({ onOpenNav }: { onOpenNav: () => void }) {
  const breadcrumbs = useBreadcrumbs();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  // The chevron always jumps to the immediate parent (second-to-last crumb), the
  // conventional "back" target — the page's own Back buttons were removed in favor of
  // this, so it must exist and be tappable whenever a parent level is available.
  const parent = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : undefined;
  const parentTo = parent ? (parent.path ?? parent.href) : undefined;

  return (
    <header className="sticky top-0 z-20 flex h-12 shrink-0 items-center justify-between gap-2 border-b bg-background/95 px-3 sm:px-5 backdrop-blur">
      <div className="flex min-w-0 items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden -ml-1 shrink-0"
          aria-label={t('nav.open_menu')}
          onClick={onOpenNav}
        >
          <Menu className="h-5 w-5" />
        </Button>
        {parentTo && (
          <Link
            to={parentTo}
            aria-label={t('common.back')}
            title={t('common.back')}
            className="-ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </Link>
        )}
        <nav className="flex min-w-0 items-center gap-1 text-sm">
          {breadcrumbs.map((item, index) => {
            const to = item.path ?? item.href;
            return (
              <span key={index} className="flex min-w-0 items-center gap-1">
                {index > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />}
                {to ? (
                  <Link
                    to={to}
                    className="truncate font-medium text-muted-foreground hover:text-foreground hover:underline transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="truncate font-semibold text-foreground">{item.label}</span>
                )}
              </span>
            );
          })}
        </nav>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {/* Who else is online is staff information a till has no business showing. */}
        {!user?.isPosOperator && <RealtimeIndicator />}
        <span className="hidden text-xs text-muted-foreground mr-1 sm:inline">{today}</span>
        <NotificationBell />
        <LanguageSwitcher />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={toggleTheme}
          title={theme === 'light' ? t('common.theme_dark') : t('common.theme_light')}
        >
          {theme === 'light' ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </header>
  );
}
